/* ============================================================
   FinishSetupCard — TASK-214, issue #332 (owner decision 2026-09-02)
   The prominent "Finish setup" block at the top of the dashboard for the
   cohort the preview paywall (TASK-143) lets in without onboarding: no
   brand profile yet, or an onboarding session still in_progress. Short
   progress (brand profile / content plan / social accounts — all from
   data the dashboard already loads) + one CTA back to /onboarding, which
   resumes an in-progress session and keeps its own Skip.

   Not a nudge: OnboardingNudge (TASK-144, the bell) stays as the
   frequent-but-dismissable reminder on every /app screen; this card is
   the dashboard's own setup surface and simply disappears once setup is
   complete. Show/hide lives in lib/finishSetup.js (pure, tested); this
   component only renders the state it is given.
   ============================================================ */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icons as I } from '../pack-icons';
import { BrandIcons } from '../pack-icons/brands';
import { track } from '../../lib/funnel.js';
import { platformLabel } from '../../lib/operations.js';
import { CONNECTABLE_PLATFORMS } from '../../lib/platforms.js';
import QuickBrandSheet from '../QuickBrandSheet.jsx';

// Per-step secondary actions. Brand resumes onboarding directly; the plan is
// still produced by onboarding and therefore has no separate row action.
const STEP_LINKS = {
  brand: '/onboarding',
  social: '/app/settings?tab=social',
  firstPost: '/app/create',
  prepareBrand: '/onboarding',
};

// No `lock` glyph in the shared pack-icons set (checked 2026-09-09) — small
// local fallback for the posting card's optional-brand footer note.
const LockGlyph = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="10.5" width="16" height="9.5" rx="2" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
  </svg>
);

// Row-1 platform chips (variant A, owner decision 2026-09-09; tiles unified
// with Library's connect-filter chips 2026-09-09 — see Library.jsx's
// `ob-net-ic-<platform>` span): the real brand marks from pack-icons/brands.jsx,
// never invented glyphs, each on its own brand-gradient tile (the same
// `ob-net-ic-*` classes onboarding.css already defines) — before step 1 is
// done this shows every connectable platform (see lib/platforms.js, the same
// list Settings' connect grid renders); once done it shows only the
// platforms the user actually connected (deduped, from `socialAccounts`).
function PlatformChips({ platforms, listLabel }) {
  return (
    <span className="fs-chips" role="list" aria-label={listLabel}>
      {platforms.map((platform) => {
        const Icon = BrandIcons[platform];
        if (!Icon) return null;
        return (
          <span
            key={platform}
            className={`fs-chip ob-net-ic-${platform}`}
            title={platformLabel(platform)}
            aria-label={platformLabel(platform)}
          >
            {Icon({ size: 11 })}
          </span>
        );
      })}
    </span>
  );
}

export default function FinishSetupCard({ state, onDismissBare }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { resume, posting, steps, done, total, optional, socialAccounts, bareWorkspace } = state;
  // Defensive fallback only — finishSetupState() already computes activeKey
  // for the variant-A posting branch (steps = social, firstPost). Kept here
  // so an unexpected shape (e.g. the separate no-brand-yet posting path,
  // which this redesign doesn't touch) still lights up exactly one row
  // instead of leaving every CTA ghost.
  const activeKey = state.activeKey ?? steps.find((step) => !step.done)?.key ?? null;
  const activeIndex = Math.max(0, steps.findIndex((step) => step.key === activeKey));
  const [quickBrandOpen, setQuickBrandOpen] = useState(false);

  // One impression per mount (the card is not re-mounted while it stays visible).
  useEffect(() => {
    try { track('setup_card_shown', { resume, done, total }); } catch { /* fail-silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clicked = (action) => {
    try { track('setup_card_clicked', { action, resume }); } catch { /* fail-silent */ }
  };

  const goOnboarding = () => {
    clicked('onboarding');
    navigate('/onboarding');
  };

  // Generic row copy for BOTH variants — posting gets its own (shorter)
  // label/sub for social + firstPost; every other key (the non-posting
  // checklist, and the separate no-brand-yet posting path) keeps the
  // original copy untouched.
  const stepText = (step) => {
    if (step.key === 'brand') {
      return { label: t('finishSetup.brandLabel'), sub: t(step.done ? 'finishSetup.brandDone' : 'finishSetup.brandTodo') };
    }
    if (step.key === 'plan') {
      return { label: t('finishSetup.planLabel'), sub: t(step.done ? 'finishSetup.planDone' : 'finishSetup.planTodo') };
    }
    if (step.key === 'firstPost') {
      if (posting) {
        return { label: t('finishSetup.firstPostLabelPosting'), sub: t(step.done ? 'finishSetup.firstPostDone' : 'finishSetup.firstPostTodoPosting') };
      }
      return { label: t('finishSetup.firstPostLabel'), sub: t(step.done ? 'finishSetup.firstPostDone' : 'finishSetup.firstPostTodo') };
    }
    if (step.key === 'prepareBrand') {
      return { label: t('finishSetup.prepareBrandLabel'), sub: t('finishSetup.prepareBrandTodo') };
    }
    if (posting) {
      return { label: t('finishSetup.socialLabelPosting'), sub: step.done ? t('finishSetup.socialDone', { count: step.count }) : null };
    }
    return {
      label: t('finishSetup.socialLabel'),
      sub: step.done ? t('finishSetup.socialDone', { count: step.count }) : t('finishSetup.socialTodo'),
    };
  };

  const connectedPlatforms = Array.from(new Set(
    (socialAccounts || []).map((acc) => String(acc?.platform || '').toLowerCase()).filter(Boolean)
  ));

  return (
    <section
      className={['card', 'fr-checklist', 'finish-setup', posting ? 'finish-setup--posting' : 'finish-setup--guided'].join(' ')}
      role="region"
      aria-labelledby="finish-setup-title"
    >
      <div className="fr-cl-head">
        <div className="finish-setup__lead">
          <div className="eyebrow"><span>{t('finishSetup.eyebrow')}</span></div>
          <h3 id="finish-setup-title">{t(posting ? 'finishSetup.titlePosting' : 'finishSetup.title')}</h3>
          <p className="finish-setup__sub">
            {t(posting ? 'finishSetup.subPosting' : bareWorkspace ? 'finishSetup.subBare' : 'finishSetup.sub')}
          </p>
        </div>
        {posting ? (
          <div className="finish-setup__progress">
            <span className="finish-setup__count">{t('finishSetup.stepOf', { n: activeIndex + 1, total })}</span>
            <div className="fs-segs" role="presentation">
              {steps.map((step, i) => (
                <i key={step.key} className={i <= activeIndex ? 'fs-seg is-on' : 'fs-seg'} />
              ))}
            </div>
          </div>
        ) : (
          <div className="finish-setup__progress">
            {/* Dismiss ("×") — ONLY for the bare-workspace case (criterion 2);
                a genuinely missing brand never renders this. Hides the card
                for 7 days via lib/finishSetupDismiss.js + lib/finishSetup.js's
                isBareWorkspaceHidden; re-appears after. */}
            {bareWorkspace && (
              <button
                type="button"
                className="finish-setup__close"
                aria-label={t('finishSetup.hideBare')}
                onClick={() => { clicked('hide_bare'); onDismissBare?.(); }}
              >
                <I.x size={12} />
              </button>
            )}
            <span className="finish-setup__count">{t('finishSetup.progress', { done, total })}</span>
            <div className="fs-segs" role="progressbar" aria-label={t('finishSetup.progress', { done, total })} aria-valuemin={0} aria-valuemax={total} aria-valuenow={done}>
              {steps.map((step) => (
                <i key={step.key} className={['fs-seg', step.done && 'is-on', !step.done && step.key === activeKey && 'is-current'].filter(Boolean).join(' ')} />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="fr-cl-list">
        {steps.map((step, index) => {
          const { label, sub } = stepText(step);
          const to = !step.done ? STEP_LINKS[step.key] : null;

          // The guided checklist shares Scheduler's numbered rows and
          // platform marks while keeping its existing onboarding actions.
          if (!posting) {
            return (
              <div key={step.key} className={['fr-cl-item', step.done && 'is-done', !step.done && step.key === activeKey && 'is-current'].filter(Boolean).join(' ')}>
                <span className="fr-cl-check">
                  {step.done ? <I.check size={12} /> : index + 1}
                </span>
                <div className="fr-cl-text">
                  <div className="fr-cl-title">{label}</div>
                  {step.key === 'social' ? (
                    <div className="fs-social-line">
                      <PlatformChips
                        platforms={step.done ? connectedPlatforms : CONNECTABLE_PLATFORMS.map((p) => p.id)}
                        listLabel={t('finishSetup.socialTodo')}
                      />
                      {sub ? <span className="fr-cl-sub">{sub}</span> : null}
                    </div>
                  ) : <div className="fr-cl-sub">{sub}</div>}
                </div>
                {step.done && step.key === 'firstPost' && step.firstPostId ? (
                  <div className="finish-setup__row-actions">
                    <span className="pill pill-success" style={{ height: 22 }}>
                      <I.check size={10} />{t('finishSetup.statusDone')}
                    </span>
                    <Link
                      to={`/content/${encodeURIComponent(step.firstPostId)}/preview`}
                      className="finish-setup__text-action"
                      onClick={() => clicked('open_post')}
                    >
                      {t('finishSetup.openPost')}
                    </Link>
                  </div>
                ) : step.done ? (
                  <span className="pill pill-success" style={{ height: 22 }}>
                    <I.check size={10} />{t('finishSetup.statusDone')}
                  </span>
                ) : null}
                {to && step.key === 'brand' && (
                  <div className="finish-setup__row-actions finish-setup__brand-actions">
                    <Link to={to} className="btn btn-soft btn-sm" onClick={() => clicked(step.key)}>
                      {t('finishSetup.brandAction')} <I.arrowRight size={11} />
                    </Link>
                    <button type="button" className="finish-setup__text-action" onClick={() => { clicked('post_as_yourself'); setQuickBrandOpen(true); }}>
                      {t('quickBrand.personalAction')}
                    </button>
                  </div>
                )}
                {to && step.key !== 'brand' && (
                  <Link to={to} className="btn btn-soft btn-sm" onClick={() => clicked(step.key)}>
                    {t(step.key === 'social' ? 'finishSetup.socialCta' : step.key === 'firstPost' ? 'finishSetup.firstPostCta' : 'finishSetup.prepareBrandCta')} <I.arrowRight size={11} />
                  </Link>
                )}
              </div>
            );
          }

          // Variant A posting row: numbered ring instead of a dot, the ONE
          // active (not-done, activeKey) row gets the accent wash + the
          // single gold CTA; every other not-done row is a small dimmed
          // ghost action so there is never more than one gold button.
          const isCurrent = !step.done && step.key === activeKey;
          const ctaLabel = step.key === 'social' ? t('misc.connectAccount') : step.key === 'firstPost' ? t('finishSetup.firstPostCta') : t('finishSetup.brandAction');
          const rowClass = ['fr-cl-item', step.done && 'is-done', isCurrent && 'is-current'].filter(Boolean).join(' ');
          return (
            <div key={step.key} className={rowClass}>
              <span className="fr-cl-check">
                {step.done ? <I.check size={12} /> : index + 1}
              </span>
              <div className="fr-cl-text">
                <div className="fr-cl-title">{label}</div>
                {step.key === 'social' ? (
                  // Tiles + the "N connected" copy read as one compact line
                  // (owner fix 2026-09-09, item 2) instead of the chips row
                  // and the sub-text stacking as two separate block lines.
                  <div className="fs-social-line">
                    <PlatformChips
                      platforms={step.done ? connectedPlatforms : CONNECTABLE_PLATFORMS.map((p) => p.id)}
                      listLabel={t('finishSetup.socialTodo')}
                    />
                    {sub ? <span className="fr-cl-sub">{sub}</span> : null}
                  </div>
                ) : (
                  sub ? <div className="fr-cl-sub">{sub}</div> : null
                )}
              </div>
              {step.done && step.key === 'firstPost' && step.firstPostId ? (
                <div className="finish-setup__row-actions">
                  <span className="pill pill-success" style={{ height: 22 }}>
                    <I.check size={10} />{t('finishSetup.statusDone')}
                  </span>
                  <Link
                    to={`/content/${encodeURIComponent(step.firstPostId)}/preview`}
                    className="finish-setup__text-action"
                    onClick={() => clicked('open_post')}
                  >
                    {t('finishSetup.openPost')}
                  </Link>
                </div>
              ) : step.done ? (
                <span className="pill pill-success" style={{ height: 22 }}>
                  <I.check size={10} />{t('finishSetup.statusDone')}
                </span>
              ) : isCurrent ? (
                <Link to={to} className="btn btn-primary" onClick={() => clicked(step.key === 'social' ? 'connect_account' : step.key)}>
                  {ctaLabel} <I.arrowRight size={13} />
                </Link>
              ) : (
                <Link to={to} className="btn btn-ghost btn-sm" onClick={() => clicked(step.key)}>
                  {ctaLabel} <I.arrowRight size={11} />
                </Link>
              )}
            </div>
          );
        })}
      </div>

      {posting && optional && (
        <div className="fs-foot">
          <span className="fs-foot-lock"><LockGlyph />{t('finishSetup.optionalBrandNote')}</span>
          <Link to={STEP_LINKS.prepareBrand} className="btn btn-soft btn-sm fs-foot-cta" onClick={() => clicked('prepareBrand')}>
            {t('finishSetup.prepareBrandCta')} <I.arrowRight size={11} />
          </Link>
        </div>
      )}

      {!posting && <div className="finish-setup__actions">
        <button type="button" className="btn btn-primary finish-setup__cta" onClick={goOnboarding}>
          {t(resume ? 'finishSetup.resumeCta' : 'finishSetup.cta')} <I.arrowRight size={13} />
        </button>
        <span className="finish-setup__hint">{t(posting ? 'finishSetup.hintPosting' : 'finishSetup.hint')}</span>
      </div>}
      {!posting && <QuickBrandSheet
        open={quickBrandOpen}
        source="setup_card"
        onClose={() => setQuickBrandOpen(false)}
        onCreated={() => setQuickBrandOpen(false)}
      />}
    </section>
  );
}
